//ADMIN PANNEL

import { IOrganisation, IOrganisationRegistration, IMember, MemberRole, IGroup, MemberCode } from "../types/organisation";
import { BaseCRUDManager } from "./base/BaseCRUDManager";
import { OrganisationCreateData, OrganisationUpdateData, AnonymousCodeParams } from "./organisation/types";

// Import modular functions
import { registerOrganisation as registerOrganisationInternal } from "./organisation/registration";
import {
  getOrganisationMembers as getOrganisationMembersInternal,
  removeMember as removeMemberInternal,
  addMemberWithAccessCode as addMemberWithAccessCodeInternal,
  getUserOrganisations as getUserOrganisationsInternal,
  getUserLoginMembership as getUserLoginMembershipInternal,
} from "./organisation/members";
import {
  verifyAccessCode as verifyAccessCodeInternal,
  inviteMemberByEmail as inviteMemberByEmailInternal,
  generateAccessCode as generateAccessCodeInternal,
  generateAnonymousMemberCode as generateAnonymousMemberCodeInternal
} from "./organisation/accessCodes";
import {
  createGroup as createGroupInternal,
  getOrganisationGroups as getOrganisationGroupsInternal,
  updateGroupMembers as updateGroupMembersInternal,
  deleteGroup as deleteGroupInternal
} from "./organisation/groups";
import {
  saveGeneratedCodes as saveGeneratedCodesInternal,
  getGeneratedCodes as getGeneratedCodesInternal,
  deleteAllGeneratedCodes as deleteAllGeneratedCodesInternal
} from "./organisation/generatedCodes";
import {
  uploadOrganisationLogo as uploadOrganisationLogoInternal,
  uploadPartnerLogos as uploadPartnerLogosInternal,
  getOrganisationLogos as getOrganisationLogosInternal
} from "./organisation/logos";
import { cleanupTestData as cleanupTestDataInternal } from "./organisation/cleanup";

export class OrganisationManager {
  // Animal emojis for student identification
  static readonly ANIMAL_EMOJIS = [
    '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯',
    '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🦆', '🦅', '🦉', '🦋',
    '🐢', '🐙', '🦈', '🦒', '🦡', '🦃', '🐝', '🐞', '🦕',
    '🐳', '🐬', '🦭', '🐘', '🦬', '🦩', '🦚', '🦜', '🐧', '🐦',
    '🦢', '🦫', '🦦', '🦥', '🦨', '🦘', '🦙', '🦛', '🐪', '🐫'
  ];

  // ==================== Organisation CRUD Operations ====================

  /**
   * Register a new organisation with admin user
   */
  static async registerOrganisation(data: IOrganisationRegistration & { existingUser?: boolean }) {
    return registerOrganisationInternal(data);
  }

  /**
   * Get an organisation by ID
   */
  static async getOrganisation(orgId: string): Promise<IOrganisation> {
    try {
      // Validate orgId
      if (!orgId || orgId.trim() === '') {
        throw new Error('Invalid organisation ID provided');
      }
      
      const organisation = await BaseCRUDManager.getById<IOrganisation>('Organisations', orgId);
      if (!organisation) {
        console.warn(`Organisation document not found for ID: ${orgId}`);
        throw new Error('Organisation not found');
      }
      return organisation;
    } catch (error) {
      console.error('Error getting Organisation:', error);
      throw error;
    }
  }

  /**
   * Check if an organisation exists
   */
  static async organisationExists(orgId: string): Promise<boolean> {
    try {
      if (!orgId || orgId.trim() === '') {
        return false;
      }
      
      return await BaseCRUDManager.exists('Organisations', orgId);
    } catch (error) {
      console.error('Error checking if organisation exists:', error);
      return false;
    }
  }

  /**
   * Get all organisations
   */
  static async getAllOrganisations(): Promise<IOrganisation[]> {
    try {
      return await BaseCRUDManager.getAll<IOrganisation>('Organisations');
    } catch (error) {
      console.error('Error getting all organisations:', error);
      throw error;
    }
  }

  /**
   * Update an organisation
   */
  static async updateOrganisation(
    orgId: string,
    data: OrganisationUpdateData
  ): Promise<void> {
    try {
      await BaseCRUDManager.update<IOrganisation>('Organisations', orgId, data);
    } catch (error) {
      console.error('Error updating organisation:', error);
      throw error;
    }
  }

  // ==================== Member Management ====================

  /**
   * Get all members of an organisation
   */
  static async getOrganisationMembers(orgId: string): Promise<IMember[]> {
    return getOrganisationMembersInternal(orgId);
  }

  /**
   * Get user's organisations
   */
  static async getUserOrganisations(userId: string) {
    return getUserOrganisationsInternal(userId);
  }

  /**
   * Organisation memberships plus whether the user has an artist/author profile.
   */
  static async getUserLoginMembership(userId: string) {
    return getUserLoginMembershipInternal(userId);
  }

  /**
   * Remove a member from an organisation
   */
  static async removeMember(orgId: string, memberId: string): Promise<void> {
    return removeMemberInternal(orgId, memberId);
  }

  /**
   * Add a member to an organisation using an access code
   */
  static async addMemberWithAccessCode(accessCode: string, userId: string): Promise<IMember> {
    return addMemberWithAccessCodeInternal(accessCode, userId);
  }

  // ==================== Access Code Management ====================

  /**
   * Verify an access code
   */
  static async verifyAccessCode(accessCode: string) {
    return verifyAccessCodeInternal(accessCode);
  }

  /**
   * Invite a member by email
   */
  static async inviteMemberByEmail(
    orgId: string,
    email: string,
    role: MemberRole
  ): Promise<string> {
    return inviteMemberByEmailInternal(orgId, email, role);
  }

  /**
   * Generate an access code for an organisation
   */
  static async generateAccessCode(orgId: string, role: MemberRole): Promise<string> {
    return generateAccessCodeInternal(orgId, role);
  }

  /**
   * Generate an anonymous member code
   */
  static async generateAnonymousMemberCode(
    orgId: string,
    identifier: string,
    animalEmoji: string,
    groupId: string,
    moderatorEmail: string
  ) {
    const params: AnonymousCodeParams = {
      orgId,
      identifier,
      animalEmoji,
      groupId,
      moderatorEmail
    };
    return generateAnonymousMemberCodeInternal(params);
  }

  // ==================== Group Management ====================

  /**
   * Create a new group
   */
  static async createGroup(orgId: string, data: { name: string; memberIds: string[] }): Promise<IGroup> {
    return createGroupInternal(orgId, data);
  }

  /**
   * Get all groups for an organisation
   */
  static async getOrganisationGroups(orgId: string): Promise<IGroup[]> {
    return getOrganisationGroupsInternal(orgId);
  }

  /**
   * Update group members
   */
  static async updateGroupMembers(
    orgId: string,
    groupId: string,
    memberIds: string[]
  ): Promise<boolean> {
    return updateGroupMembersInternal(orgId, groupId, memberIds);
  }

  /**
   * Delete a group
   */
  static async deleteGroup(organisationId: string, groupId: string): Promise<void> {
    return deleteGroupInternal(organisationId, groupId);
  }

  // ==================== Generated Codes Management ====================

  /**
   * Save generated codes for an organisation
   */
  static async saveGeneratedCodes(orgId: string, codes: MemberCode[]): Promise<void> {
    return saveGeneratedCodesInternal(orgId, codes);
  }

  /**
   * Get generated codes for an organisation
   */
  static async getGeneratedCodes(orgId: string) {
    return getGeneratedCodesInternal(orgId);
  }

  /**
   * Delete all generated codes for an organisation
   */
  static async deleteAllGeneratedCodes(organisationId: string): Promise<void> {
    return deleteAllGeneratedCodesInternal(organisationId);
  }

  // ==================== Logo Management ====================

  /**
   * Upload organisation logo
   */
  static async uploadOrganisationLogo(orgId: string, logoDataUrl: string): Promise<void> {
    return uploadOrganisationLogoInternal(orgId, logoDataUrl);
  }

  /**
   * Upload partner logos
   */
  static async uploadPartnerLogos(orgId: string, logoDataUrls: string[]): Promise<void> {
    return uploadPartnerLogosInternal(orgId, logoDataUrls);
  }

  /**
   * Get organisation logos
   */
  static async getOrganisationLogos(organisationId: string): Promise<{ orgLogoUrl?: string, partnerLogoUrls: string[] }> {
    return getOrganisationLogosInternal(organisationId);
  }

  // ==================== Cleanup Operations ====================

  /**
   * Clean up test data for an organisation
   */
  static async cleanupTestData(orgId: string): Promise<void> {
    return cleanupTestDataInternal(orgId);
  }
}
